/**
 * One payment, and what became of the money.
 *
 * The page is arranged around the thing the status column cannot say. A PARTIAL refund
 * leaves the payment `CAPTURED` — the amount goes back, the status does not move — so the
 * header states the amount, what has already gone back, and what is left, rather than
 * making an admin work it out from three fields.
 *
 * The other half is the story. A payment is a sequence of events (taken, then refunded,
 * possibly several times, by different people for different reasons) and the old page
 * showed two timestamps and an unordered list. Reading "was this refunded before or after
 * the customer complained" meant comparing dates by eye.
 *
 * Refunding is real money and cannot be undone from here: super-admin only, enforced
 * server-side too, and behind an explicit confirm rather than a single press.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  CreditCard,
  IndianRupee,
  Loader2,
  Receipt,
  RotateCcw,
  Store,
  Undo2,
  Unlink,
  User,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import { ApiError } from '@/lib/api';
import { use_is_super_admin } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { ago, date_time, inr, short_id, title_case } from '@/lib/format';
import {
  use_payment_detail,
  use_refund_payment,
  use_settle_refund,
} from '@/features/payments/hooks';
import {
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TONE,
  SUBJECT_LABEL,
  is_outstanding,
  is_partly_refunded,
  money_story,
  type AdminPaymentDetail,
} from '@/features/payments/types';

export function PaymentDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: p, isLoading, error } = use_payment_detail(id);
  const is_super_admin = use_is_super_admin();
  const [settling, set_settling] = useState<string | null>(null);

  if (error) {
    return (
      <DashboardLayout title="Payment" breadcrumbs={[{ label: short_id(id) }]}>
        <ErrorState
          title="Could not load this payment"
          description="It may have been removed, or the reference is wrong."
        />
      </DashboardLayout>
    );
  }

  const refundable = p ? Number(p.refundable_inr) : 0;

  return (
    <DashboardLayout
      /*
       * The amount is the identity. The old page was titled "Payment" with a cuid
       * underneath — two lines that told an operator nothing about which payment they
       * were looking at.
       */
      title={p ? inr(p.amount_inr) : 'Payment'}
      subtitle={p ? money_story(p) : undefined}
      breadcrumbs={[{ label: p ? short_id(p.id) : short_id(id) }]}
      title_badges={
        p ? (
          <>
            <Badge tone={PAYMENT_STATUS_TONE[p.status]} variant="solid" dot data-testid="payment-status">
              {PAYMENT_STATUS_LABEL[p.status]}
            </Badge>
            {/* The state the status column has no word for. */}
            {is_partly_refunded(p) && (
              <Badge tone="warning" data-testid="payment-partial">
                <Undo2 className="h-3 w-3" /> Part refunded
              </Badge>
            )}
            {p.is_dummy && (
              <Badge tone="neutral" data-testid="payment-dummy">
                Test payment
              </Badge>
            )}
          </>
        ) : undefined
      }
      meta={
        p?.subject?.customer ? (
          <Link
            to={`/users/${p.subject.customer.id}`}
            className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-primary hover:underline"
          >
            <User className="h-3.5 w-3.5" />
            {p.subject.customer.name ?? p.subject.customer.phone ?? 'Customer'}
          </Link>
        ) : undefined
      }
      stats={<MetricStrip metrics={build_metrics(p)} />}
    >
      <div className="grid items-start gap-gutter lg:grid-cols-[1.3fr_1fr]">
        <div className="grid gap-gutter">
          <StoryPanel
            payment={p}
            loading={isLoading}
            can_settle={is_super_admin}
            on_settle={set_settling}
          />

          {/*
            Refunding moves real money and cannot be undone from here, so it is super-admin
            only (enforced server-side too) and needs an explicit confirm rather than a
            single button press.
          */}
          {is_super_admin && p && refundable > 0 && <RefundPanel payment={p} />}
        </div>

        <div className="grid gap-gutter">
          <DetailPanel title="What it paid for" icon={Receipt} test_id="payment-subject">
            {isLoading || !p ? (
              <PanelSkeleton rows={4} />
            ) : !p.subject ? (
              /*
               * Money the platform took and cannot attribute. Called out rather than left
               * as an empty panel — an empty panel reads as a loading bug.
               */
              <PanelEmpty icon={Unlink}>
                Nothing is attached to this payment — no booking, plan, package, adoption
                request or order. That should not happen, and it is worth investigating.
              </PanelEmpty>
            ) : (
              <FieldList>
                <Field
                  label="Item"
                  value={
                    p.subject.link_path ? (
                      <Link
                        to={p.subject.link_path}
                        className="underline-offset-2 hover:text-primary hover:underline"
                        data-testid="subject-link"
                      >
                        {p.subject.label}
                      </Link>
                    ) : (
                      // Walk plans and training packages have no console page yet.
                      <span className="flex flex-col">
                        {p.subject.label}
                        <span className="text-micro text-muted-foreground">
                          No console page for these yet
                        </span>
                      </span>
                    )
                  }
                />
                <Field label="Kind" value={SUBJECT_LABEL[p.subject.kind]} />
                <Field label="Its status" value={title_case(p.subject.status)} />
                <Field
                  label="Vendor"
                  value={
                    p.subject.vendor ? (
                      <Link
                        to={`/vendors/${p.subject.vendor.id}`}
                        className="inline-flex items-center gap-1.5 underline-offset-2 hover:text-primary hover:underline"
                      >
                        <Store className="h-3.5 w-3.5" />
                        {p.subject.vendor.business_name ?? short_id(p.subject.vendor.id)}
                      </Link>
                    ) : null
                  }
                  empty="Nobody is paid out on this"
                />
                <Field
                  label="Customer"
                  value={
                    p.subject.customer ? (
                      <Link
                        to={`/users/${p.subject.customer.id}`}
                        className="underline-offset-2 hover:text-primary hover:underline"
                      >
                        {p.subject.customer.name ?? p.subject.customer.phone ?? 'No name on file'}
                      </Link>
                    ) : null
                  }
                />
                <Field label="Item ID" value={p.subject.id} mono />
              </FieldList>
            )}
          </DetailPanel>

          <DetailPanel title="At the gateway" icon={CreditCard} test_id="payment-gateway">
            {isLoading || !p ? (
              <PanelSkeleton rows={4} />
            ) : (
              <FieldList>
                <Field label="Provider" value={p.provider} />
                <Field
                  label="Kind"
                  value={
                    p.is_dummy ? (
                      <span className="text-muted-foreground">
                        Test payment — no real money moved
                      </span>
                    ) : (
                      'Real money'
                    )
                  }
                />
                <Field label="Order reference" value={p.order_ref} mono empty="None issued" />
                <Field
                  label="Payment reference"
                  value={p.provider_ref}
                  mono
                  /*
                   * "Never captured" is only true when it never was. A seeded or older
                   * captured payment can simply have no reference stored, and telling an
                   * admin the money never arrived would be a lie about the ledger.
                   */
                  empty={p.captured_at ? 'Not recorded' : 'Never captured'}
                />
                {p.failure_reason && (
                  <Field
                    label="Why it failed"
                    value={<span className="text-destructive">{p.failure_reason}</span>}
                  />
                )}
                <Field label="Payment ID" value={p.id} mono />
              </FieldList>
            )}
          </DetailPanel>
        </div>
      </div>

      {p && settling && (
        <SettleModal
          payment={p}
          refund_id={settling}
          on_close={() => set_settling(null)}
        />
      )}
    </DashboardLayout>
  );
}

function build_metrics(p: AdminPaymentDetail | undefined): Metric[] {
  const refunded = p ? Number(p.refunded_amount_inr) : 0;
  const refundable = p ? Number(p.refundable_inr) : 0;
  const outstanding = (p?.refunds ?? [])
    .filter(is_outstanding)
    .reduce((sum, r) => sum + Number(r.amount_inr), 0);

  return [
    {
      key: 'amount',
      label: 'Charged',
      value: p ? inr(p.amount_inr) : undefined,
      hint: p?.captured_at ? `taken ${ago(p.captured_at)}` : 'never taken',
      icon: IndianRupee,
      loading: !p,
    },
    {
      key: 'refunded',
      label: 'Given back',
      value: p ? inr(p.refunded_amount_inr) : undefined,
      /*
       * "₹900 · 0 refunds" is a contradiction. Refund auditing arrived after some refunds
       * had already happened, so those exist only as a total on the payment — say that
       * rather than reporting a count that argues with the amount beside it.
       */
      hint: !p
        ? undefined
        : refunded > 0 && p.refunds.length === 0
          ? 'no breakdown recorded'
          : `${p.refunds.length} refund${p.refunds.length === 1 ? '' : 's'}`,
      tone: refunded > 0 ? 'warning' : 'default',
      icon: RotateCcw,
      loading: !p,
    },
    {
      key: 'owed',
      label: 'Still owed',
      /*
       * Refunded on paper and not confirmed sent. Distinct from "given back" above it:
       * one is what we decided, this is what has not actually left.
       */
      value: p ? inr(String(outstanding)) : undefined,
      hint: outstanding > 0 ? 'Not confirmed as sent' : 'Everything sent',
      tone: outstanding > 0 ? 'destructive' : 'default',
      icon: AlertTriangle,
      loading: !p,
    },
    {
      key: 'kept',
      label: 'Kept',
      // The number the status cannot give you on a part-refunded payment.
      value: p ? inr(p.refundable_inr) : undefined,
      hint: refundable > 0 && refunded > 0 ? 'still refundable' : undefined,
      icon: Banknote,
      loading: !p,
    },
    {
      key: 'status',
      label: 'Where it stands',
      value: p ? PAYMENT_STATUS_LABEL[p.status] : undefined,
      hint: p ? money_story(p) : undefined,
      tone:
        p?.status === 'FAILED' ? 'destructive' : p?.status === 'PENDING' ? 'warning' : 'default',
      icon: CheckCircle2,
      loading: !p,
    },
  ];
}

/**
 * What happened to the money, in order.
 *
 * A payment is a sequence of events — taken, then refunded, possibly several times, by
 * different people for different reasons. The old page had two timestamps in a field list
 * and an unordered refund list, so "was this refunded before or after the customer
 * complained" meant comparing dates by eye.
 */
function StoryPanel({
  payment,
  loading,
  can_settle,
  on_settle,
}: {
  payment: AdminPaymentDetail | undefined;
  loading: boolean;
  can_settle: boolean;
  on_settle: (refund_id: string) => void;
}) {
  if (loading || !payment) {
    return (
      <DetailPanel title="What happened to the money" icon={RotateCcw}>
        <PanelSkeleton rows={3} />
      </DetailPanel>
    );
  }

  type Event = {
    at: string;
    title: string;
    detail?: string;
    amount?: string;
    tone: 'neutral' | 'success' | 'warning' | 'destructive';
    /** Set on the "refund decided" event, so the row can offer to settle it. */
    refund?: AdminPaymentDetail['refunds'][number];
  };

  const events: Event[] = [
    {
      at: payment.created_at,
      title: 'Checkout started',
      detail: `${inr(payment.amount_inr)} requested through ${payment.provider}`,
      tone: 'neutral',
    },
  ];

  if (payment.captured_at) {
    events.push({
      at: payment.captured_at,
      title: 'Money taken',
      amount: inr(payment.amount_inr),
      detail: payment.provider_ref ? `Gateway reference ${payment.provider_ref}` : undefined,
      tone: 'success',
    });
  } else if (payment.status === 'FAILED') {
    events.push({
      at: payment.created_at,
      title: 'The gateway refused it',
      detail: payment.failure_reason ?? 'No reason recorded',
      tone: 'destructive',
    });
  }

  for (const r of payment.refunds) {
    events.push({
      at: r.created_at,
      title: `Refund decided by ${title_case(r.actor_type)}`,
      amount: `−${inr(r.amount_inr)}`,
      detail: r.reason ?? 'No reason recorded',
      tone: 'warning',
      refund: r,
    });
    /*
     * Settlement is its own event, at its own time. A refund decided on Monday and paid on
     * Friday is two facts, and collapsing them into one row is precisely how "we owe this"
     * gets mistaken for "we paid this".
     */
    if (r.settled_at) {
      events.push({
        at: r.settled_at,
        title: 'Money confirmed sent',
        detail: [r.settlement_ref, r.settlement_note].filter(Boolean).join(' · '),
        tone: 'success',
      });
    }
  }

  events.sort((a, b) => a.at.localeCompare(b.at));

  /*
   * A total with no breakdown behind it. Refund auditing was added after some refunds had
   * already happened, so those rows exist only as a number on the payment — said out loud
   * rather than leaving a timeline that silently disagrees with the tile above it.
   */
  const unaudited =
    Number(payment.refunded_amount_inr) > 0 && payment.refunds.length === 0;

  /** Refunds decided and not confirmed sent — what the customer is still owed. */
  const outstanding_total = payment.refunds
    .filter(is_outstanding)
    .reduce((sum, r) => sum + Number(r.amount_inr), 0);

  return (
    <DetailPanel
      title="What happened to the money"
      icon={RotateCcw}
      description={money_story(payment)}
      test_id="payment-story"
    >
      <ol className="flex flex-col gap-0" data-testid="payment-timeline">
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`} className="flex gap-3">
            {/* The rail: a dot per event, joined except after the last. */}
            <span className="flex flex-col items-center">
              <span
                className={cn(
                  'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                  e.tone === 'success' && 'bg-success',
                  e.tone === 'warning' && 'bg-warning',
                  e.tone === 'destructive' && 'bg-destructive',
                  e.tone === 'neutral' && 'bg-muted-foreground',
                )}
              />
              {i < events.length - 1 && <span className="w-px flex-1 bg-border" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col pb-4">
              <span className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-heading text-data font-bold">{e.title}</span>
                {e.amount && (
                  <span
                    className={cn(
                      'font-semibold tabular',
                      e.tone === 'warning' ? 'text-destructive' : 'text-foreground',
                    )}
                  >
                    {e.amount}
                  </span>
                )}
              </span>
              {e.detail && (
                <span className="break-words text-micro text-muted-foreground">{e.detail}</span>
              )}
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-micro text-muted-foreground" title={date_time(e.at)}>
                  {ago(e.at)}
                </span>
                {/*
                  The distinction this whole column exists for: a refund row means the money
                  was DECIDED, not that it left. Until somebody confirms the transfer, the
                  customer is still owed it.
                */}
                {e.refund && is_outstanding(e.refund) && (
                  <Badge
                    tone="destructive"
                    size="sm"
                    dot
                    data-testid={`refund-outstanding-${e.refund.id}`}
                  >
                    Still owed
                  </Badge>
                )}
                {e.refund && is_outstanding(e.refund) && can_settle && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-micro"
                    onClick={() => on_settle(e.refund!.id)}
                    data-testid={`settle-${e.refund.id}`}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Mark as sent
                  </Button>
                )}
              </span>
            </span>
          </li>
        ))}
      </ol>

      {unaudited && (
        <p className="mt-1 rounded-control bg-muted px-2.5 py-2 text-micro text-muted-foreground">
          {inr(payment.refunded_amount_inr)} is recorded against this payment with no
          breakdown — it predates refund auditing, so there is no record of who did it or why.
        </p>
      )}

      {/*
        Said plainly, because it is the difference between "the customer has their money"
        and "the customer is owed their money" — and nothing in this console tracks which.
      */}
      {outstanding_total > 0 ? (
        <p
          className="mt-2 rounded-control bg-destructive/10 px-2.5 py-2 text-micro font-medium text-destructive"
          data-testid="outstanding-summary"
        >
          {inr(String(outstanding_total))} has been refunded on paper and not confirmed as
          sent. The customer is still owed it until somebody marks it.
        </p>
      ) : payment.refunds.length > 0 ? (
        <p className="mt-2 text-micro text-muted-foreground" data-testid="all-settled">
          Every refund on this payment has been confirmed as sent.
        </p>
      ) : null}
    </DetailPanel>
  );
}

function RefundPanel({ payment }: { payment: AdminPaymentDetail }) {
  const refund_m = use_refund_payment(payment.id);
  const [amount, set_amount] = useState('');
  const [reason, set_reason] = useState('');
  const [form_error, set_form_error] = useState<string | null>(null);
  const [confirming, set_confirming] = useState(false);

  const refundable = Number(payment.refundable_inr);
  const amount_n = Number(amount);
  const amount_valid = amount.trim() !== '' && amount_n > 0 && amount_n <= refundable;
  const can_submit = amount_valid && reason.trim().length >= 5 && !refund_m.isPending;

  function submit() {
    set_form_error(null);
    refund_m.mutate(
      { amount_inr: amount_n, reason: reason.trim() },
      {
        onSuccess: () => {
          notify.success(`${inr(String(amount_n))} refund recorded`, {
            description: 'Settle it with the gateway — this console does not do that for you.',
          });
          set_amount('');
          set_reason('');
          set_confirming(false);
        },
        onError: (err) =>
          set_form_error(err instanceof ApiError ? err.message : 'Could not record this refund.'),
      },
    );
  }

  const reset = () => set_confirming(false);

  return (
    <DetailPanel
      title="Refund some of this"
      icon={Undo2}
      description={`Up to ${inr(payment.refundable_inr)} is still refundable`}
      test_id="refund-card"
    >
      <div className="grid gap-3">
        <p className="max-w-prose text-data text-muted-foreground">
          The refund is recorded against your account and cannot be reversed here. It is
          settled with the gateway separately — recording it does not move the money.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="refund-amount">Amount</Label>
            <div className="relative">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-data text-muted-foreground"
              >
                ₹
              </span>
              <input
                id="refund-amount"
                type="number"
                min={0}
                max={refundable}
                step="0.01"
                className="console-field w-40 pl-6 tabular [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                value={amount}
                onChange={(e) => {
                  set_amount(e.target.value);
                  reset();
                }}
                data-testid="refund-amount"
              />
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              set_amount(String(refundable));
              reset();
            }}
            data-testid="refund-full"
          >
            All {inr(payment.refundable_inr)}
          </Button>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="refund-reason">Why</Label>
          <textarea
            id="refund-reason"
            rows={2}
            maxLength={300}
            className="console-field min-h-16 py-2"
            value={reason}
            onChange={(e) => {
              set_reason(e.target.value);
              reset();
            }}
            placeholder="Stored on the payment, and read by whoever reconciles this later."
            data-testid="refund-reason"
          />
        </div>

        {amount.trim() !== '' && !amount_valid && (
          <p className="text-micro font-medium text-destructive">
            Enter an amount between ₹1 and {inr(payment.refundable_inr)}.
          </p>
        )}
        {form_error && (
          <p className="text-micro font-medium text-destructive" data-testid="refund-error">
            {form_error}
          </p>
        )}

        {confirming ? (
          <div className="flex flex-wrap items-center gap-3 rounded-control border border-destructive/30 bg-destructive/5 px-3 py-2.5">
            <span className="text-data font-semibold">
              Refund {inr(String(amount_n))} of {inr(payment.amount_inr)}? This cannot be undone.
            </span>
            <Button
              variant="destructive"
              size="sm"
              disabled={refund_m.isPending}
              onClick={submit}
              data-testid="refund-confirm"
            >
              {refund_m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Yes, refund it
            </Button>
            <Button variant="ghost" size="sm" onClick={reset}>
              Cancel
            </Button>
          </div>
        ) : (
          <div>
            <Button
              disabled={!can_submit}
              onClick={() => set_confirming(true)}
              data-testid="refund-submit"
            >
              <Undo2 className="mr-1 h-4 w-4" /> Record a refund
            </Button>
          </div>
        )}
      </div>
    </DetailPanel>
  );
}

/**
 * Confirming a refund actually reached the customer.
 *
 * The reference is required rather than optional. "Marked as sent by somebody, at some
 * point, with nothing to look up" is barely better than not recording it at all — the
 * whole value of this column is being able to answer a customer who says the money never
 * arrived, and that answer is a reference number.
 *
 * It records bookkeeping, not a transfer: the money moves in a bank or in the gateway's own
 * dashboard, and the copy says so rather than letting an admin believe pressing this pays
 * anybody.
 */
function SettleModal({
  payment,
  refund_id,
  on_close,
}: {
  payment: AdminPaymentDetail;
  refund_id: string;
  on_close: () => void;
}) {
  const settle_m = use_settle_refund(payment.id);
  const refund = payment.refunds.find((r) => r.id === refund_id);
  const [reference, set_reference] = useState('');
  const [note, set_note] = useState('');
  const [error, set_error] = useState<string | null>(null);

  if (!refund) return null;

  async function submit() {
    set_error(null);
    if (reference.trim().length < 3) {
      return set_error('Enter the bank or gateway reference for the transfer.');
    }
    try {
      await settle_m.mutateAsync({
        refund_id,
        settlement_ref: reference.trim(),
        note: note.trim() || undefined,
      });
      notify.success(`${inr(refund!.amount_inr)} marked as sent`, {
        description: `Reference ${reference.trim()}`,
      });
      on_close();
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Could not record the settlement.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mark refund as sent"
      data-testid="settle-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-panel border border-border bg-card p-5 shadow-l2">
        <h2 className="font-heading text-base font-bold">Mark {inr(refund.amount_inr)} as sent</h2>
        <p className="mt-1 text-data text-muted-foreground">
          This records that the money reached the customer. It does not move any money —
          the transfer happens in your bank or the gateway.
        </p>

        <div className="mt-3 grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="settlement-ref">Transfer reference</Label>
            <input
              id="settlement-ref"
              autoFocus
              className="console-field"
              placeholder="UTR, NEFT reference or gateway refund id"
              value={reference}
              onChange={(e) => set_reference(e.target.value)}
              data-testid="settlement-ref"
            />
            <p className="text-micro text-muted-foreground">
              What you would quote back if the customer says it never arrived.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="settlement-note">Note (optional)</Label>
            <input
              id="settlement-note"
              className="console-field"
              placeholder="Anything the next person should know"
              value={note}
              onChange={(e) => set_note(e.target.value)}
              data-testid="settlement-note"
            />
          </div>

          {error && (
            <p className="text-micro font-medium text-destructive" data-testid="settle-error">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => void submit()}
              disabled={settle_m.isPending}
              data-testid="settle-confirm"
            >
              {settle_m.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1 h-4 w-4" />
              )}
              Record it as sent
            </Button>
            <Button variant="ghost" onClick={on_close} disabled={settle_m.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
